import React, { useMemo } from 'react';

import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { EventArg } from '@react-navigation/native';

import { SweepsTabIcon } from '@/assets/icons/tab/SweepsTabIcon';
import { LobbyIcon } from '@/assets/icons/tab/home';
import { SearchTabIcon } from '@/assets/icons/tab/search';
import { useHandleSocialLink } from '@/feature/entry-share/hooks/use-share-link-import';
import { usePrefetchGeoToken } from '@/feature/lobby-casino/hooks/use-casino-session';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { CasinoSearchScreen } from '@/feature/search/screens/CasinoSearch';
import { useAppInit } from '@/hooks/use-app-init';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useOneLinkNavigation } from '@/hooks/use-one-link-navigation';

import { AccountTabConfig, mapTabsFromTabSettings } from '../tabs';
import { LobbyStack } from './lobbyStack';
import { CasinoTabNavigatorParamList } from './types';
import { useCasinoTabsNavigator } from './use-casino-tabs-navigator-props';

const TabCasino = createBottomTabNavigator<CasinoTabNavigatorParamList>();

const CasinoTabsConfig = [
    { name: 'CasinoLobby', component: LobbyStack, icon: LobbyIcon, title: 'Home' },
    { name: 'CasinoSearch', component: CasinoSearchScreen, icon: SearchTabIcon, title: 'Search' },
] as const;

const EmptyScreen = () => null;

const ProductSwitcherTab = {
    name: 'SwitchToSweeps',
    component: EmptyScreen,
    icon: () => <SweepsTabIcon />,
    title: 'Sportsbook',
    listeners: () => ({
        tabPress: (e: EventArg<'tabPress', true>) => {
            // Prevent default navigation to the tab's screen
            e.preventDefault();
            useProductTransitionStore.getState().actions.triggerTransition(Product.Sweeps);
        },
    }),
} as const;

export const CasinoTabsNavigator = () => {
    useOneLinkNavigation();
    useHandleSocialLink(Product.Casino);

    const tabSettings = useJurisdictionStore(state => state.jurisdictionSettings?.productConfig?.tabSettings);
    const showSweepsSwitchTab = useShouldShowSweepsSwitchTab();

    const tabsConfig = useMemo(() => {
        return mapTabsFromTabSettings(
            [...CasinoTabsConfig, showSweepsSwitchTab ? ProductSwitcherTab : null, AccountTabConfig].filter(Boolean),
            tabSettings
        );
    }, [tabSettings, showSweepsSwitchTab]);

    const { geocomplyClientReady } = useAppInit();
    usePrefetchGeoToken(geocomplyClientReady);

    const { screenOptions, tabBar } = useCasinoTabsNavigator();

    return (
        <TabCasino.Navigator screenOptions={screenOptions} tabBar={tabBar}>
            {tabsConfig.map(tab => (
                <TabCasino.Screen key={tab.name} {...tab} options={{ tabBarIcon: tab.icon, title: tab.title }} />
            ))}
        </TabCasino.Navigator>
    );
};

/**
 * Determines if we should render the Sweeps switch tab on Casino.
 * Only shows when Sweeps product is available in the jurisdiction.
 */
const useShouldShowSweepsSwitchTab = () => {
    const { isProductAvailable } = useAvailableProducts();
    const isSweepsAvailable = isProductAvailable(Product.Sweeps);
    return isSweepsAvailable;
};
